import { makeStyles, mergeClasses, shorthands, Text, tokens } from '@fluentui/react-components';
import { ShieldTask16Regular } from '@fluentui/react-icons';
import { FC, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { FeatureKeys } from '../../../redux/features/app/AppState';
import { setSelectedConversation } from '../../../redux/features/conversations/conversationsSlice';
import { Breakpoints, customTokens, SharedStyles } from '../../../styles';
import { EditChatName } from '../shared/EditChatName';
import { ListItemActions } from './ListItemActions';

const useClasses = makeStyles({
    root: {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        cursor: 'pointer',
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingVerticalM),
        ...shorthands.margin(0, tokens.spacingVerticalS),
        position: 'relative',
        '&:hover .actions': {
            opacity: 1,
        },
        '&.selected .actions': {
            opacity: 1,
        },
    },
    actions: {
        opacity: 0,
        transition: 'opacity 0.2s ease',
        position: 'absolute',
        right: tokens.spacingHorizontalM,
        top: '50%',
        transform: 'translateY(-50%)',
        '&.visible': {
            opacity: 1,
        },
    },
    avatar: {
        flexShrink: 0,
        width: '32px',
    },
    body: {
        minWidth: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        marginLeft: tokens.spacingHorizontalXS,
        alignSelf: 'center',
    },
    header: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    title: {
        ...SharedStyles.overflowEllipsis,
        fontSize: tokens.fontSizeBase300,
        color: customTokens.colorCustomText,
    },
    timestamp: {
        flexShrink: 0,
        marginLeft: tokens.spacingHorizontalM,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        lineHeight: tokens.lineHeightBase200,
    },
    previewText: {
        ...SharedStyles.overflowEllipsis,
        display: 'block',
        lineHeight: tokens.lineHeightBase100,
        color: tokens.colorNeutralForeground2,
    },
    popoverSurface: {
        display: 'none',
        ...Breakpoints.small({
            display: 'flex',
            flexDirection: 'column',
        }),
    },
    selected: {
        backgroundColor: customTokens.colorSelectedItem,
        borderRadius: tokens.borderRadiusXLarge,
    },
    protectedIcon: {
        color: tokens.colorPaletteLightGreenBorder1,
        verticalAlign: 'text-bottom',
        marginLeft: tokens.spacingHorizontalXS,
    },
});

interface IChatListItemProps {
    id: string;
    header: string;
    preview: string;
    botProfilePicture: string;
    isSelected: boolean;
    selectedAgentId: string;
}

export const ChatListItem: FC<IChatListItemProps> = ({ id, header, preview, isSelected, selectedAgentId }) => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const { features } = useAppSelector((state: RootState) => state.app);

    const showPreview = !features[FeatureKeys.SimplifiedExperience].enabled && preview;
    //const showActions = features[FeatureKeys.SimplifiedExperience].enabled && isSelected;

    const [editingTitle, setEditingTitle] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const onClick = (_ev: any) => {
        dispatch(setSelectedConversation(id));
    };

    return (
        <div
            className={mergeClasses(classes.root, isSelected && classes.selected)}
            onClick={onClick}
            title={`Chat: ${header}`}
            aria-label={`Chat list item: ${header}`}
        >
            {editingTitle ? (
                <EditChatName
                    name={header}
                    chatId={id}
                    agentId={selectedAgentId}
                    exitEdits={() => {
                        setEditingTitle(false);
                    }}
                />
            ) : (
                <>
                    <div className={classes.body}>
                        <div className={classes.header}>
                            <Text className={classes.title} title={header}>
                                {header}
                                {features[FeatureKeys.AzureContentSafety].enabled && (
                                    <ShieldTask16Regular className={classes.protectedIcon} />
                                )}
                            </Text>
                        </div>
                        {showPreview && (
                            <>
                                {
                                    <Text id={`message-preview-${id}`} size={200} className={classes.previewText}>
                                        {preview}
                                    </Text>
                                }
                            </>
                        )}
                    </div>
                    <div className={`${classes.actions} actions ${menuOpen ? 'visible' : ''}`}>
                        <ListItemActions
                            chatId={id}
                            agentId={selectedAgentId}
                            onEditTitleClick={() => {
                                setEditingTitle(true);
                            }}
                            onMenuOpenChange={(open: boolean) => {
                                setMenuOpen(open);
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
